if (!customElements.get('astra-mobile-buy')) {
  customElements.define('astra-mobile-buy', class extends HTMLElement {
    connectedCallback() {
      this.start = () => {
        this.product = document.getElementById('MainProduct-' + this.dataset.section);
        if (!this.product) return;
        this.action = this.querySelector('[data-sticky-add]');
        this.lastY = window.scrollY;
        this.directionUp = true;
        this.buttonAbove = false;
        this.hidden = false;
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = 0;
            const y = window.scrollY;
            if (Math.abs(y - this.lastY) > 5) {
              this.directionUp = y < this.lastY;
              this.lastY = y;
            }
            this.updateVisibility();
          });
        };
        this.scheduleSync = () => {
          if (this.syncFrame) return;
          this.syncFrame = requestAnimationFrame(() => {
            this.syncFrame = 0;
            this.syncProduct();
          });
        };
        this.onClick = () => this.button?.click();
        this.action.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        document.addEventListener('focusin', this.onScroll);
        document.addEventListener('focusout', this.onScroll);
        this.observer = new MutationObserver(this.scheduleSync);
        this.observer.observe(this.product, {
          subtree: true, childList: true, characterData: true,
          attributes: true, attributeFilter: ['disabled', 'src', 'srcset']
        });
        this.syncProduct();
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.start, { once: true });
      } else this.start();
    }

    syncProduct() {
      const button = this.product.querySelector('product-form form button[name="add"]');
      if (!button) return;
      if (this.button !== button) {
        this.button = button;
        this.buttonObserver?.disconnect();
        this.buttonObserver = new IntersectionObserver(entries => {
          this.buttonAbove = entries[0].boundingClientRect.bottom < 0;
          this.updateVisibility();
        }, {
          // Keep the button inside the observed area while it is below the
          // viewport, so an anchor jump past it still crosses a boundary.
          rootMargin: '0px 0px ' + Math.ceil(document.documentElement.scrollHeight) + 'px 0px'
        });
        this.buttonObserver.observe(button);
      }
      if (this.action.disabled !== button.disabled) this.action.disabled = button.disabled;
      this.setText(this.action, button.disabled ? 'Indisponible' : 'Ajouter');
      const price = this.product.querySelector('[id^="price-"] .price-item--regular');
      if (price) this.setText(this.querySelector('[data-sticky-price]'), price.textContent.trim());
      const title = this.product.querySelector('h1');
      if (title) this.setText(this.querySelector('[data-sticky-name]'), title.textContent.trim());
      const current = this.product.querySelector('.product__media-item.is-active img');
      if (current) {
        const image = this.querySelector('img');
        const preview = this.product.querySelector('[data-astra-variant-preview]');
        [image, preview].filter(Boolean).forEach(target => {
          let src = current.src;
          let srcset = current.srcset;
          const url = new URL(src, document.baseURI);
          if (url.pathname.startsWith('/cdn/shop/') || url.hostname === 'cdn.shopify.com') {
            const widths = target === preview ? [180, 360, 600] : [60, 120, 180];
            const sources = widths.map(width => {
              url.searchParams.set('width', width);
              return { src: url.href, width };
            });
            src = sources[sources.length - 1].src;
            srcset = sources.map(source => source.src + ' ' + source.width + 'w').join(', ');
          }
          if (target.srcset !== srcset) target.srcset = srcset;
          if (target.src !== src) target.src = src;
          if (target.alt !== current.alt) target.alt = current.alt;
        });
      }
      this.updateVisibility();
    }

    setText(element, text) {
      if (element && element.textContent !== text) element.textContent = text;
    }

    updateVisibility() {
      const active = document.activeElement;
      const typing = active && /INPUT|TEXTAREA|SELECT/.test(active.tagName);
      const visible = this.buttonAbove && (innerWidth >= 750 || this.directionUp) && !typing;
      if (visible === this.visible) return;
      this.visible = visible;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
      this.setAttribute('aria-hidden', String(!visible));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.buttonObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.syncFrame);
      this.action?.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('focusin', this.onScroll);
      document.removeEventListener('focusout', this.onScroll);
      document.removeEventListener('DOMContentLoaded', this.start);
    }
  });
}

if (!customElements.get('astra-curated-gallery')) {
  customElements.define('astra-curated-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.astra-curated-track');
      this.views = [...this.querySelectorAll('[data-curated-open]')];
      this.thumbs = [...this.querySelectorAll('[data-curated-go]')];
      this.dialog = this.querySelector('dialog');
      this.index = 0;
      this.selected = 0;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show((this.index + (event.key === 'ArrowRight' ? 1 : this.views.length - 1)) % this.views.length);
        }
      }, { signal });
      this.nearObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.prepare();
        this.nearObserver.disconnect();
      }, { rootMargin: '1200px 0px' });
      this.nearObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.track);
      this.section = this.closest('[id^="AstraGallery-"]');
      this.onNavigation = event => {
        const link = event.target.closest?.('a[href]');
        if (link && this.section && link.hash === '#' + this.section.id) this.prepare();
      };
      document.addEventListener('pointerover', this.onNavigation, { passive: true, signal });
      document.addEventListener('focusin', this.onNavigation, { signal });
      document.addEventListener('pointerdown', this.onNavigation, { passive: true, signal });
    }

    prepare() {
      if (this.prepared) return;
      this.prepared = true;
      this.thumbs.forEach((button, index) => {
        const image = button.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        this.views[index].style.backgroundImage = 'url(' + JSON.stringify(image.src) + ')';
        this.views[index].style.backgroundSize = 'contain';
        this.views[index].style.backgroundPosition = 'center';
        this.views[index].style.backgroundRepeat = 'no-repeat';
        const full = this.views[index].querySelector('img');
        const clear = () => this.views[index].style.removeProperty('background-image');
        if (full.complete && full.naturalWidth) clear();
        else full.addEventListener('load', clear, { once: true, signal: this.abort.signal });
      });
      this.loadNear(this.selected);
      this.scheduleMeasure();
    }

    loadNear(index) {
      [index, index - 1, index + 1].forEach(i => {
        const image = this.views[i]?.querySelector('img');
        if (image && image.loading !== 'eager') image.loading = 'eager';
      });
    }

    scheduleMeasure() {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => {
        const trackRect = this.track.getBoundingClientRect();
        this.trackWidth = this.track.clientWidth;
        this.centers = this.views.map(view => {
          const rect = view.getBoundingClientRect();
          return rect.left - trackRect.left + this.track.scrollLeft + rect.width / 2;
        });
      });
    }

    select(index) {
      if (index === this.selected) return;
      this.thumbs[this.selected]?.setAttribute('aria-pressed', 'false');
      this.thumbs[index]?.setAttribute('aria-pressed', 'true');
      this.selected = index;
      this.loadNear(index);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.centers?.length) return;
        const center = this.track.scrollLeft + this.trackWidth / 2;
        let nearest = 0;
        this.centers.forEach((value, i) => {
          if (Math.abs(value - center) < Math.abs(this.centers[nearest] - center)) nearest = i;
        });
        this.select(nearest);
      });
    }

    onClick(event) {
      // Closing an image is not a form submission: avoid form-tracking work.
      if (event.target.closest('[data-curated-close]')) {
        this.dialog.close();
        return;
      }
      const open = event.target.closest('[data-curated-open]');
      const thumb = event.target.closest('[data-curated-go]');
      if (open) {
        this.show(+open.dataset.curatedOpen);
        this.dialog.showModal();
      } else if (thumb) {
        const index = +thumb.dataset.curatedGo;
        this.prepare();
        this.loadNear(index);
        const left = this.centers?.[index] - this.trackWidth / 2;
        if (Number.isFinite(left)) this.track.scrollTo({ left: Math.max(0, left), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      } else if (event.target.closest('[data-curated-next]')) {
        this.show((this.index + 1) % this.views.length);
      } else if (event.target.closest('[data-curated-prev]')) {
        this.show((this.index + this.views.length - 1) % this.views.length);
      } else if (event.target.closest('[data-curated-enlarge]')) {
        const zoom = this.dialog.classList.toggle('is-enlarged');
        const button = event.target.closest('button');
        button.setAttribute('aria-pressed', String(zoom));
        button.textContent = zoom ? 'Réduire −' : 'Zoom ＋';
        if (zoom) requestAnimationFrame(() => {
          const canvas = this.querySelector('.astra-curated-zoom-canvas');
          canvas.scrollTo((canvas.scrollWidth - canvas.clientWidth) / 2, (canvas.scrollHeight - canvas.clientHeight) / 2);
        });
      }
    }

    show(index) {
      this.index = index;
      const view = this.views[index];
      const image = this.dialog.querySelector('img');
      image.src = view.dataset.fullImage;
      image.alt = view.querySelector('img').alt;
      this.dialog.classList.remove('is-enlarged');
      const zoom = this.querySelector('[data-curated-enlarge]');
      zoom.setAttribute('aria-pressed', 'false');
      zoom.textContent = 'Zoom ＋';
      this.querySelector('[data-curated-status]').textContent = (index + 1) + ' / ' + this.views.length;
      this.querySelector('.astra-curated-zoom-canvas').scrollTo(0, 0);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.nearObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.measureFrame);
      this.prepared = false;
    }
  });
}

